// PatientList.js - Patienthanteringslista

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPatients, getClinicUsers } from '../Calendar/calendarApi';
import NewPatientModal from '../common/NewPatientModal';
import { UserContext } from '../../UserContext';
import { formatPnrPasis } from '../../utils/personnummer';
import './PatientList.css';

const PatientList = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(UserContext);

  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sök och filter
  const [search, setSearch] = useState('');
  const [clinicUsers, setClinicUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Sortering
  const [sortBy, setSortBy] = useState('lastname');
  const [sortOrder, setSortOrder] = useState('asc');

  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(50);

  // Ny patient modal
  const [showNewPatient, setShowNewPatient] = useState(false);

  // Ladda behandlare
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await getClinicUsers();
        const filteredUsers = users.filter(u => u.clinic_id === currentUser?.clinic_id);
        setClinicUsers(filteredUsers);
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };
    if (currentUser) {
      loadUsers();
    }
  }, [currentUser]);

  // Ladda patienter
  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getPatients(search, {
        user_id: selectedUserId || undefined,
        limit,
        offset: page * limit,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      setPatients(result.patients || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Error loading patients:', err);
      setError('Kunde inte ladda patienter');
    } finally {
      setLoading(false);
    }
  }, [search, selectedUserId, page, limit, sortBy, sortOrder]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadPatients();
    }, 300);
    return () => clearTimeout(debounce);
  }, [loadPatients]);

  // Sortering
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0);
  };

  // Klicka på patient
  const handlePatientClick = (patient) => {
    navigate(`/patients/${patient.patient_id}`);
  };

  // Ny (eller redan befintlig) patient → öppna patienten
  const openPatient = (patient) => {
    setShowNewPatient(false);
    navigate(`/patients/${patient.patient_id}`);
  };

  // Formatera datum
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  // Sorteringsikon
  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <i className="fas fa-sort sort-icon neutral"></i>;
    return sortOrder === 'asc'
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  return (
    <div className="patient-list-container">
      {/* Header */}
      <div className="patient-list-header">
        <h2>Patienter</h2>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowNewPatient(true)}>
            <i className="fas fa-plus"></i> Ny patient
          </button>
        </div>
      </div>

      {/* Sök och filter */}
      <div className="patient-list-filters">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Sök på namn, personnummer, telefon eller e-post..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="clear-search" onClick={() => setSearch('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className="filter-group">
          <label>Behandlare:</label>
          <select
            value={selectedUserId}
            onChange={(e) => { setSelectedUserId(e.target.value); setPage(0); }}
          >
            <option value="">Alla</option>
            {clinicUsers.map(user => (
              <option key={user.user_id} value={user.user_id}>
                {user.first_name} {user.last_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="patient-list-error">{error}</div>}

      {/* Tabell */}
      <div className="patient-table-wrapper">
        <table className="patient-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('firstname')} className="sortable">
                Förnamn <SortIcon column="firstname" />
              </th>
              <th onClick={() => handleSort('lastname')} className="sortable">
                Efternamn <SortIcon column="lastname" />
              </th>
              <th onClick={() => handleSort('personnummer')} className="sortable">
                Personnummer <SortIcon column="personnummer" />
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                E-post <SortIcon column="email" />
              </th>
              <th>Telefon</th>
              <th>Senaste bokning</th>
              <th>Antal bokn.</th>
            </tr>
          </thead>
          <tbody>
            {loading && patients.length === 0 ? (
              <tr>
                <td colSpan="7" className="loading-cell">
                  <i className="fas fa-spinner fa-spin"></i> Laddar...
                </td>
              </tr>
            ) : patients.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-cell">
                  {search ? 'Inga patienter matchade sökningen' : 'Inga patienter registrerade'}
                </td>
              </tr>
            ) : (
              patients.map(patient => (
                <tr
                  key={patient.patient_id}
                  onClick={() => handlePatientClick(patient)}
                  className="patient-row"
                >
                  <td className="name-cell">{patient.firstname}</td>
                  <td className="name-cell">{patient.lastname}</td>
                  <td>{formatPnrPasis(patient.personnummer, patient.id_type)}</td>
                  <td className="email-cell">{patient.email || '-'}</td>
                  <td>{patient.phone || '-'}</td>
                  <td>{formatDate(patient.last_appointment)}</td>
                  <td className="count-cell">{patient.appointment_count || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="patient-pagination">
          <button
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            <i className="fas fa-chevron-left"></i> Föregående
          </button>
          <span>
            Visar {page * limit + 1}-{Math.min((page + 1) * limit, total)} av {total}
          </span>
          <button
            disabled={(page + 1) * limit >= total}
            onClick={() => setPage(p => p + 1)}
          >
            Nästa <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Ny patient — gemensamt formulär (SPAR först för klinik med SPAR) */}
      <NewPatientModal
        isOpen={showNewPatient}
        onClose={() => setShowNewPatient(false)}
        onCreated={openPatient}
        onExisting={openPatient}
      />
    </div>
  );
};

export default PatientList;
